import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// httpOnly cookie based auth — no token in localStorage.
// `withCredentials: true` ensures the cookie is sent on every request.
const client = axios.create({ baseURL: API_BASE, withCredentials: true });

export const api = {
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  signup: (body) => client.post("/auth/signup", body).then((r) => r.data),
  brand: () => client.get("/brand").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  users: () => client.get("/users").then((r) => r.data),

  tenantMe: () => client.get("/tenants/me").then((r) => r.data),
  tenantBrandUpdate: (body) => client.patch("/tenants/me/brand", body).then((r) => r.data),
  tenantLogoUpload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/tenants/me/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  tenantLogoUrl: (tenantId) => tenantId ? `${API_BASE}/tenants/${tenantId}/logo` : null,

  team: () => client.get("/team").then((r) => r.data),
  teamInvite: (body) => client.post("/team/invite", body).then((r) => r.data),
  teamUpdate: (id, body) => client.patch(`/team/${id}`, body).then((r) => r.data),
  teamRemove: (id) => client.delete(`/team/${id}`).then((r) => r.data),

  leadsList: () => client.get("/leads").then((r) => r.data),
  leadCreate: (body) => client.post("/leads", body).then((r) => r.data),
  leadUpdate: (id, body) => client.patch(`/leads/${id}`, body).then((r) => r.data),

  projectsList: () => client.get("/projects").then((r) => r.data),
  projectCreate: (body) => client.post("/projects", body).then((r) => r.data),
  projectUpdate: (id, body) => client.patch(`/projects/${id}`, body).then((r) => r.data),
  projectDelete: (id) => client.delete(`/projects/${id}`).then((r) => r.data),
  project: (id) => client.get(`/projects/${id}`).then((r) => r.data),
  projectSettings: (id, body) => client.patch(`/projects/${id}/settings`, body).then((r) => r.data),
  addRoom: (id, name) => client.post(`/projects/${id}/rooms`, { name }).then((r) => r.data),
  deleteRoom: (projectId, roomId) => client.delete(`/projects/${projectId}/rooms/${roomId}`).then((r) => r.data),

  boqAdd: (projectId, item, roomId) => client.post(`/projects/${projectId}/boq`, item, { params: { room_id: roomId } }).then((r) => r.data),
  boqUpdate: (itemId, body) => client.patch(`/boq/${itemId}`, body).then((r) => r.data),
  boqDelete: (itemId) => client.delete(`/boq/${itemId}`).then((r) => r.data),

  estimate: (id) => client.get(`/projects/${id}/estimate`).then((r) => r.data),
  estimateSendOtp: (projectId, tier) => client.post(`/projects/${projectId}/estimate/send-otp`, { tier }).then((r) => r.data),
  estimateApprove: (projectId, otp, tier) => client.post(`/projects/${projectId}/estimate/approve`, { otp, tier }).then((r) => r.data),
  estimateApproval: (projectId) => client.get(`/projects/${projectId}/estimate/approval`).then((r) => r.data),

  invoicesList: () => client.get("/invoices").then((r) => r.data),
  invoice: (id) => client.get(`/invoices/${id}`).then((r) => r.data),
  invoiceCreate: (body) => client.post("/invoices", body).then((r) => r.data),
  invoiceSend: (id) => client.post(`/invoices/${id}/send`).then((r) => r.data),
  invoiceMockPay: (id, amount) => client.post(`/invoices/${id}/mock-pay`, { amount }).then((r) => r.data),

  notifications: () => client.get("/notifications").then((r) => r.data),
  notificationsMarkRead: () => client.post("/notifications/mark-read").then((r) => r.data),

  execution: (projectId) => client.get(`/projects/${projectId}/execution`).then((r) => r.data),
  taskCreate: (projectId, body) => client.post(`/projects/${projectId}/tasks`, body).then((r) => r.data),
  taskUpdate: (id, body) => client.patch(`/tasks/${id}`, body).then((r) => r.data),
  taskDelete: (id) => client.delete(`/tasks/${id}`).then((r) => r.data),
  taskPhotos: (id) => client.get(`/tasks/${id}/photos`).then((r) => r.data),
  taskPhotoUpload: (id, file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/tasks/${id}/photos`, fd, { params: { kind }, headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  milestoneUpdate: (id, status) => client.patch(`/milestones/${id}`, { status }).then((r) => r.data),
  // Cookie-authenticated URLs — the browser will attach the httpOnly cookie automatically.
  photoUrl: (photoId) => `${API_BASE}/photos/${photoId}`,

  quotationsList: (projectId) => client.get(`/projects/${projectId}/quotations`).then((r) => r.data),
  quotationsAll: () => client.get("/quotations").then((r) => r.data),
  quotationCreate: (projectId, tier) => client.post(`/projects/${projectId}/quotations`, { tier }).then((r) => r.data),
  quotationMarkSent: (id) => client.post(`/quotations/${id}/mark-sent`).then((r) => r.data),
  quotationPdfUrl: (id) => `${API_BASE}/quotations/${id}/pdf`,

  billing: (projectId) => client.get(`/projects/${projectId}/billing`).then((r) => r.data),
  billingMilestoneLink: (bmId, siteMilestoneId) => client.post(`/billing-milestones/${bmId}/link`, { site_milestone_id: siteMilestoneId }).then((r) => r.data),
  creditNoteCreate: (body) => client.post("/credit-notes", body).then((r) => r.data),
  creditNoteUpdate: (id, body) => client.patch(`/credit-notes/${id}`, body).then((r) => r.data),
  creditNoteDelete: (id) => client.delete(`/credit-notes/${id}`).then((r) => r.data),
  billingStatementUrl: (projectId) => `${API_BASE}/projects/${projectId}/billing/statement.pdf`,

  creditNoteCreateAlias: (body) => client.post("/credit-notes", body).then((r) => r.data),

  dashboard: () => client.get("/dashboard").then((r) => r.data),

  // Vendors
  vendorsList: () => client.get("/vendors").then((r) => r.data),
  vendor: (id) => client.get(`/vendors/${id}`).then((r) => r.data),
  vendorCreate: (body) => client.post("/vendors", body).then((r) => r.data),
  vendorUpdate: (id, body) => client.patch(`/vendors/${id}`, body).then((r) => r.data),
  vendorDelete: (id) => client.delete(`/vendors/${id}`).then((r) => r.data),

  // Vendor price list
  priceList: (vendorId) => client.get(`/vendors/${vendorId}/price-list`).then((r) => r.data),
  priceItemCreate: (vendorId, body) => client.post(`/vendors/${vendorId}/price-list`, body).then((r) => r.data),
  priceItemUpdate: (itemId, body) => client.patch(`/vendors/price-items/${itemId}`, body).then((r) => r.data),
  priceItemDelete: (itemId) => client.delete(`/vendors/price-items/${itemId}`).then((r) => r.data),

  // Purchase Orders
  purchaseOrdersList: (params) => client.get("/purchase-orders", { params }).then((r) => r.data),
  purchaseOrder: (id) => client.get(`/purchase-orders/${id}`).then((r) => r.data),
  purchaseOrderCreate: (body) => client.post("/purchase-orders", body).then((r) => r.data),
  purchaseOrderUpdate: (id, body) => client.patch(`/purchase-orders/${id}`, body).then((r) => r.data),
  purchaseOrderIssue: (id) => client.post(`/purchase-orders/${id}/issue`).then((r) => r.data),
  purchaseOrderDelete: (id) => client.delete(`/purchase-orders/${id}`).then((r) => r.data),

  // GRNs
  grnCreate: (poId, body) => client.post(`/purchase-orders/${poId}/grns`, body).then((r) => r.data),
  grnsForPo: (poId) => client.get(`/purchase-orders/${poId}/grns`).then((r) => r.data),
  grnsList: () => client.get("/grns").then((r) => r.data),
};

export default client;
